/**
 * Sync queue inspector.
 *
 * The offline queue is otherwise invisible: when a practitioner says "I
 * recorded that check and it's not there", there is no way to tell a record
 * still waiting to send from one that was lost. This shows the queue directly,
 * and "Retry failed" is the only way back for an item that has stopped trying.
 *
 * Available in production deliberately. A shared tablet in a nursery is
 * precisely where records get stranded, and support cannot talk someone
 * through a screen that only exists on a developer's laptop. It shows only
 * this device's own queue, so there is nothing here its holder cannot already
 * see. "Clear Offline Data" in the user menu stays non-production — that one
 * destroys work rather than reporting on it.
 */

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { FeatherArrowLeft } from "@subframe/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import {
  offlineDb,
  type SyncQueueItem,
  WORK_TABLE_NAMES,
} from "@/lib/offline/db";
import {
  isPermanentlyFailed,
  MAX_RETRIES,
  syncService,
  type SyncStatus,
} from "@/lib/offline/syncService";
import { resolveLandingRoute } from "@/lib/resolve-landing-route";

export const Route = createFileRoute("/sync-status")({
  component: SyncStatusPage,
});

type TableCounts = { name: string; pending: number; errored: number };

/** What a queue item is called in plain words, keyed by its queue type. */
const QUEUE_TYPE_LABELS: Record<string, string> = {
  completion: "Checklist started",
  answer: "Checklist answer",
  photo: "Checklist photo",
  finalize: "Checklist completed",
  logEntry: "Log entry",
  eventRecord: "Event record",
  hotHoldingMeal: "Hot holding meal",
  hotHoldingReading: "Hot holding reading",
  incidentSignoff: "Incident sign-off",
  hotHoldingAlertAck: "Alert acknowledgement",
  compliancePhoto: "Compliance photo",
  complianceFinalize: "Compliance task",
  followUpPhoto: "Follow-up photo",
  followUpFinalize: "Follow-up action",
};

/** A queue row as the table renders it, with the entity resolved to something readable. */
type QueueRow = {
  item: SyncQueueItem;
  /** What kind of record this is — the log type's own name where we can resolve it. */
  label: string;
  /** Who captured it, when we know. Attribution is the whole point of this queue. */
  capturedBy?: string;
  /** When the work was done, as opposed to when it was queued. */
  capturedAt?: string;
};

/**
 * Resolve each queued item to something a human can act on.
 *
 * "logEntry" alone is not enough to work with once a device is holding a dozen
 * rows — it does not say whether it is a sleep check or a fridge temperature,
 * or whose work it is. Both matter when the question being asked is "where has
 * my check gone?".
 */
async function describeQueue(items: SyncQueueItem[]): Promise<QueueRow[]> {
  // The cached session list is the only local source of log type NAMES; the
  // entry itself stores just an id.
  const logTypeNames = new Map<string, string>();
  try {
    const cached = await offlineDb.logSessionsCache.toArray();
    for (const row of cached) {
      const sessions = (row.sessions ?? []) as Array<{
        logTypeId?: string;
        logTypeName?: string;
      }>;
      for (const session of sessions) {
        if (session.logTypeId && session.logTypeName) {
          logTypeNames.set(session.logTypeId, session.logTypeName);
        }
      }
    }
  } catch {
    // Cache unreadable — fall back to the generic type label below.
  }

  return Promise.all(
    items.map(async (item) => {
      const fallback = QUEUE_TYPE_LABELS[item.type] ?? item.type;

      try {
        if (item.type === "logEntry") {
          const entry = await offlineDb.logEntries.get(item.entityId);
          if (entry) {
            return {
              item,
              label: logTypeNames.get(entry.logTypeId) ?? fallback,
              capturedBy: entry.recordedByName,
              capturedAt: entry.timestamp,
            };
          }
        }

        if (item.type === "eventRecord") {
          const record = await offlineDb.eventRecords.get(item.entityId);
          if (record) {
            return {
              item,
              label: record.eventTypeName || fallback,
              capturedBy: record.recordedByName,
              capturedAt: record.timestamp,
            };
          }
        }

        if (item.type === "hotHoldingMeal") {
          const meal = await offlineDb.hotHoldingMeals.get(item.entityId);
          if (meal) {
            return {
              item,
              label: meal.mealName || fallback,
              capturedBy: meal.recordedByName,
              capturedAt: meal.timestamp,
            };
          }
        }

        if (item.type === "incidentSignoff") {
          const signoff = await offlineDb.incidentSignoffs.get(item.entityId);
          if (signoff) {
            return { item, label: fallback, capturedAt: signoff.queuedAt };
          }
        }
      } catch {
        // Any lookup failure just leaves the generic label in place.
      }

      return { item, label: fallback };
    }),
  );
}

/** "14:32 · 3m ago" — the absolute time to correlate with logs, the relative one to scan. */
function formatQueuedAt(createdAt: number): string {
  const time = new Date(createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

  const seconds = Math.max(0, Math.round((Date.now() - createdAt) / 1000));
  if (seconds < 60) return `${time} · ${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${time} · ${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${time} · ${hours}h ago`;
}

/** Rows still holding work, per table. Tables with nothing waiting are dropped. */
async function readWorkTableCounts(): Promise<TableCounts[]> {
  const counts = await Promise.all(
    WORK_TABLE_NAMES.map(async (name) => {
      const rows = await offlineDb
        .table<{ syncState?: string }>(name)
        .toArray()
        .catch(() => []);
      return {
        name,
        pending: rows.filter((r) => r.syncState === "pending").length,
        errored: rows.filter((r) => r.syncState === "error").length,
      };
    }),
  );
  return counts.filter((c) => c.pending > 0 || c.errored > 0);
}

function SyncStatusPage() {
  const navigate = useNavigate();
  const { userRoleType } = useAuth();
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [tables, setTables] = useState<TableCounts[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setStatus(await syncService.getStatus());
    setQueue(
      await describeQueue(
        await offlineDb.syncQueue.orderBy("priority").toArray(),
      ),
    );
    setTables(await readWorkTableCounts());
  }, []);

  useEffect(() => {
    void refresh();

    // The service pushes on every state change; the interval covers rows that
    // change without a sync run (a fresh capture landing in the queue).
    const unsubscribe = syncService.subscribe(() => void refresh());
    const timer = setInterval(() => void refresh(), 2_000);

    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, [refresh]);

  const runAction = useCallback(
    async (action: () => Promise<void>) => {
      setBusy(true);
      try {
        await action();
      } finally {
        setBusy(false);
        await refresh();
      }
    },
    [refresh],
  );

  return (
    <div className="min-h-screen bg-default-background">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-8 mobile:p-4">
        <header className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            {/* This route sits outside the app shell, so it has no sidebar or
                bottom nav of its own — on a tablet that left no way back at
                all. Goes to wherever this user's role belongs, so it works
                whether they navigated here or opened the URL directly. */}
            <IconButton
              icon={<FeatherArrowLeft />}
              onClick={() =>
                navigate({ to: resolveLandingRoute(userRoleType) })
              }
            />
            <h1 className="text-heading-2 font-heading-2 text-default-font">
              Sync queue
            </h1>
          </div>
          <p className="text-body font-body text-subtext-color">
            Records captured on this device that have not reached the server
            yet. Nothing here is lost — it stays on the device until it sends.
          </p>
        </header>

        <section className="grid grid-cols-2 gap-3 mobile:grid-cols-2">
          <Stat label="Waiting" value={status?.pendingCount ?? 0} />
          <Stat
            label="Out of retries"
            value={status?.errorCount ?? 0}
            tone={status?.errorCount ? "warn" : "normal"}
          />
          <Stat label="Syncing" value={status?.isSyncing ? "yes" : "no"} />
          <Stat
            label="Connection"
            value={status?.isOnline ? "online" : "offline"}
            tone={status && !status.isOnline ? "warn" : "normal"}
          />
        </section>

        <section className="flex gap-2">
          <Button
            disabled={busy}
            onClick={() => void runAction(() => syncService.immediateSync())}
          >
            Sync now
          </Button>
          <Button
            variant="neutral-secondary"
            disabled={busy || !status?.errorCount}
            onClick={() => void runAction(() => syncService.retryFailed())}
          >
            Retry failed records
          </Button>
        </section>

        <section className="flex flex-col gap-2">
          <h2 className="text-heading-3 font-heading-3 text-default-font">
            Queue ({queue.length})
          </h2>
          {queue.length === 0 ? (
            <p className="text-body font-body text-subtext-color">
              Nothing waiting — everything captured here has been sent.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-md border border-neutral-border">
              <table className="w-full min-w-[36rem] text-left text-body font-body">
                <thead className="bg-neutral-50 text-caption-bold font-caption-bold text-subtext-color">
                  <tr>
                    <th className="px-3 py-2">Record</th>
                    <th className="px-3 py-2">Queued</th>
                    <th className="px-3 py-2">Attempts</th>
                    <th className="px-3 py-2">Last error</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map(({ item, label, capturedBy, capturedAt }) => {
                    // Two different states, shown separately: "given up" means
                    // the server refused it until the retries ran out, while a
                    // parked item has not been refused at all and still goes
                    // through for the colleague it is waiting on.
                    const exhausted = isPermanentlyFailed(item);
                    const parked = item.deferredUntilAuthChange === true;
                    return (
                      <tr
                        key={item.id}
                        className="border-t border-neutral-border align-top"
                      >
                        <td className="px-3 py-2">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-default-font">{label}</span>
                            <span className="text-caption font-caption text-subtext-color">
                              {item.type}
                              {capturedBy ? ` · ${capturedBy}` : ""}
                            </span>
                            <span className="font-mono text-caption text-subtext-color">
                              {item.entityId}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-caption font-caption text-subtext-color">
                          <div className="flex flex-col gap-0.5">
                            <span>{formatQueuedAt(item.createdAt)}</span>
                            {/* When the practitioner actually did the check,
                                which can be well before it was queued. */}
                            {capturedAt ? (
                              <span>
                                captured{" "}
                                {new Date(capturedAt).toLocaleTimeString([], {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td
                          className={`px-3 py-2 ${exhausted ? "text-error-700" : "text-default-font"}`}
                        >
                          {item.attempts}
                          {exhausted ? ` / ${MAX_RETRIES} — given up` : ""}
                          {!exhausted && parked
                            ? " — waiting for an authorised sign-in"
                            : ""}
                        </td>
                        <td className="px-3 py-2 text-caption font-caption text-subtext-color">
                          {item.lastError ?? "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h2 className="text-heading-3 font-heading-3 text-default-font">
            Unsent records by type
          </h2>
          {tables.length === 0 ? (
            <p className="text-body font-body text-subtext-color">
              No unsent records on this device.
            </p>
          ) : (
            <ul className="flex flex-col gap-1">
              {tables.map((t) => (
                <li
                  key={t.name}
                  className="flex justify-between rounded-md bg-neutral-50 px-3 py-2 text-body font-body"
                >
                  <span className="text-default-font">{t.name}</span>
                  <span className="text-subtext-color">
                    {t.pending} waiting
                    {t.errored > 0 ? `, ${t.errored} errored` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  tone = "normal",
}: {
  label: string;
  value: number | string;
  tone?: "normal" | "warn";
}) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-neutral-border p-3">
      <span className="text-caption font-caption text-subtext-color">
        {label}
      </span>
      <span
        className={`text-heading-3 font-heading-3 ${tone === "warn" ? "text-error-700" : "text-default-font"}`}
      >
        {value}
      </span>
    </div>
  );
}
